import { AmountSuperScript } from "components/design/AmountSuperScript";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ArrowRight } from "components/icons/ArrowRight";
import { Info } from "components/icons/Info";
import { LinkBankAccount } from "components/LinkBankAccount";
import { getIn } from "formik";
import { useBankConnect } from "hooks/useBankConnect";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { useMemo, useState } from "react";
import {
  useConfirmAccountsMutation,
  useGetNewIntermediateBanksQuery,
} from "store/apis/bankConnections";
import { useGetCurrenciesQuery } from "store/apis/invoices";
import {
  Institution,
  useGetAllConnectionsQuery,
} from "store/apis/booksConnections";
import { BackendError } from "types/utils/error";

type IntermediateBanksError = BackendError & { data?: { message?: string } };

export const BankCheckBox = ({
  account,
  onChange,
  name,
  checked,
}: {
  account: {
    name: string;
    mask: string;
    balance?: string | number;
    current_balance?: string | number;
  };
  onChange: (value: boolean) => void;
  name: string;
  checked: boolean;
}) => {
  return (
    <div className="t-flex t-gap-3 t-items-center" key={account.name}>
      <Checkbox
        checked={checked}
        name={name}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label
        htmlFor={name}
        className="t-flex t-gap-0.5 t-flex-col t-cursor-pointer"
      >
        <span className="t-text-body-sm t-text-text-100">
          {account.name} ••• {account.mask}
        </span>
        <span className="t-text-body-sm t-text-text-30">
          Account Balance:{" "}
          <AmountSuperScript
            amount={Number(account.balance || account.current_balance || 0)}
          />
        </span>
      </label>
    </div>
  );
};

export const ConnectBankAccount = ({
  bank,
  isOpen,
  close,
  entityId,
  onSuccess,
}: {
  bank: Institution;
  isOpen: boolean;
  close: () => void;
  entityId: string;
  onSuccess?: () => void;
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { uuid: groupId } = useCurrentGroupContext();
  const { isMobile } = useWindowSize();
  const stepperSize = isMobile ? "xs" : "small";
  const [plaidToken, setPlaidToken] = useState<string | null>(null);
  const [directToken, setDirectToken] = useState<string | null>(null);

  const { data: connections } = useGetAllConnectionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId || !bank.connection_id }
  );

  const { alertToast, successToast } = useToast();

  const { onConnectPopup } = useBankConnect({
    connectionId: bank.connection_id,
    onConnectSuccess(code) {
      if (code) {
        setDirectToken(code);
        setCurrentStep(2);
      }
    },
  });
  const codePayload: Record<string, string | null> = {};

  if (bank.connection_id) {
    codePayload["code"] = directToken;
  }

  if (!bank.connection_id) {
    codePayload["public_token"] = plaidToken;
  }
  const {
    data: rawIntermediateBanksData,
    isLoading,
    isSuccess: intermediateBanksSuccess,
    isError: isIntermediateBanksError,
    error: intermediateBanksError,
  } = useGetNewIntermediateBanksQuery(
    {
      entityId,
      mapping_type: bank.connection_id ? "DIRECT" : "PLAID",
      connection_id: bank.connection_id,
      ...codePayload,
    },
    {
      skip: !entityId || (!codePayload["code"] && !codePayload["public_token"]),
    }
  );

  const intermediateBanksErrorMessage =
    (intermediateBanksError as IntermediateBanksError)?.data?.message ??
    "Something went wrong while connecting to your bank. Please check your credentials and try again.";

  const resetConnectionFlow = () => {
    setDirectToken(null);
    setPlaidToken(null);
    setCurrentStep(1);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetConnectionFlow();
    }
    close();
  };

  const { accounts, plaid_item_id, institution_id } =
    rawIntermediateBanksData ?? {};

  const onNonsyncComplete = async (publicToken: string) => {
    await confirmAccounts({
      entityId: entityId!,
      payload: {
        new_plaid_item_id: plaid_item_id,
        institution_id: institution_id,
        mapping_type: "PLAID",
        public_token: publicToken,
      },
    }).unwrap();

    onSuccess?.();
    successToast({
      message: "Bank account connected successfully.",
    });
  };

  const [confirmAccounts, { isLoading: isConfirmingMigration }] =
    useConfirmAccountsMutation();

  const { data: currenciesResponse } = useGetCurrenciesQuery();

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse) return [];
    return Object.entries(currenciesResponse).map(([code, name]) => ({
      label: `${code} - ${name}`,
      value: code,
    }));
  }, [currenciesResponse]);

  const onSubmit = async (values: {
    account_mappers: {
      mask: string;
      name: string;
      include: boolean;
      currency?: string;
    }[];
  }) => {
    try {
      const selectedAccounts = values.account_mappers
        .filter((v) => v.include)
        .map((v) => ({
          mask: v.mask,
          name: v.name,
          currency: v.currency,
        }));
      if (selectedAccounts.length === 0) {
        return;
      }

      await confirmAccounts({
        entityId: entityId!,
        payload: {
          account_mappers: selectedAccounts,
          new_plaid_item_id: plaid_item_id,
          institution_id: institution_id,
          mapping_type: bank.connection_id ? "DIRECT" : "PLAID",
          connection_id: bank.connection_id,
        },
      }).unwrap();

      onSuccess?.();
      successToast({
        message: "Bank account connected successfully.",
      });
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ||
          "An error occurred while connecting the bank account.",
      });
    }
  };

  if (!bank.connection_id) {
    return (
      <LinkBankAccount
        enableEntitySelection={false}
        sync={false}
        entityId={entityId}
        onNonSyncComplete={onNonsyncComplete}
        groupId={groupId}
      >
        {({ ready, connectPlaid }) => (
          <Button
            size="small"
            onClick={connectPlaid}
            disabled={!ready || isConfirmingMigration}
            isLoading={isConfirmingMigration}
          >
            {isConfirmingMigration ? "Connecting" : "Connect"}
          </Button>
        )}
      </LinkBankAccount>
    );
  }

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Modal.Content useCustomOverlay size="large">
          <FormikForm
            key={intermediateBanksSuccess ? "loaded" : "empty"}
            initialValues={{
              account_mappers:
                accounts?.map((a) => ({
                  ...a,
                  include: true,
                  currency: a.currency ?? "USD",
                })) || [],
            }}
            // @ts-ignore
            onSubmit={onSubmit}
          >
            {({ values, setFieldValue, submitForm }) => {
              const onCurrencyChange = (index: number, currency: string) => {
                const new_account_mappers = values.account_mappers.slice();
                new_account_mappers[index].currency = currency;
                setFieldValue("account_mappers", new_account_mappers);
              };

              const handleCurrencyChange = (
                index: number,
                option: OptionData | readonly OptionData[] | null
              ) => {
                if (option instanceof Array || !option) return;
                onCurrencyChange(index, option.value);
              };

              return (
                <>
                  <Modal.Header className="sm:t-pl-4">
                    <div>
                      <Modal.Title>Connect {bank.name}</Modal.Title>
                      <Modal.Subtitle>
                        <Stepper size={stepperSize} direction="horizontal">
                          <Stepper.Step
                            step={1}
                            isActive={currentStep >= 1}
                            clickable={currentStep >= 1}
                          >
                            <div className="t-flex t-items-center">
                              Connect bank
                              <ArrowRight color="currentColor" />
                            </div>
                          </Stepper.Step>
                          <Stepper.Step
                            step={2}
                            isActive={currentStep >= 2}
                            clickable={currentStep >= 2}
                          >
                            <div className="t-flex t-items-center">
                              Select accounts
                            </div>
                          </Stepper.Step>
                        </Stepper>
                      </Modal.Subtitle>
                    </div>
                    <Modal.Close />
                  </Modal.Header>

                  <Modal.Body className="t-flex t-flex-col t-gap-6 sm:t-px-4">
                    <div className="t-flex t-flex-col t-gap-6">
                      {currentStep === 1 && (
                        <div>
                          <p className="t-mb-4 t-text-text-60 t-text-body-sm">
                            Connect your bank to Inkle to automatically import
                            your accounts & transactions.
                          </p>
                          <div className="t-flex t-gap-1.5 t-items-start t-mb-6">
                            <span className="t-text-text-30 t-flex t-mt-0.5">
                              <Info
                                stroke="1.4"
                                color="currentColor"
                                size="12"
                              />
                            </span>

                            <p className="t-m-0 t-italic t-text-body-sm-italic t-text-text-60 sm:t-text-caption-italic">
                              By continuing, you'll be redirected to your bank's
                              login to complete a secure connection. Once done,
                              return here to finish the setup.
                            </p>
                          </div>
                          <div className="t-justify-center t-flex">
                            {bank.connection_id && (
                              <Button
                                onClick={() =>
                                  onConnectPopup({
                                    connectionId: bank.connection_id!,
                                    entityId,
                                  })
                                }
                                customType="primary"
                              >
                                Connect bank
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {currentStep === 2 && (
                        <Async.Root
                          isEmpty={accounts?.length === 0}
                          isLoading={isLoading}
                          isSuccess={intermediateBanksSuccess}
                          isError={isIntermediateBanksError}
                        >
                          <Async.ErrorHandler>
                            <div className="t-h-28 t-flex t-flex-col t-gap-4 t-items-start">
                              <p className="t-m-0 t-text-body-sm t-text-red">
                                {intermediateBanksErrorMessage}
                              </p>
                              <Button
                                size="small"
                                type="button"
                                onClick={resetConnectionFlow}
                              >
                                Try again
                              </Button>
                            </div>
                          </Async.ErrorHandler>
                          <Async.Empty>
                            <div className="t-h-28">
                              <p className="t-m-0 t-text-body-sm t-text-text-30">
                                No accounts found for this bank connection.
                                Please ensure that you have successfully
                                connected your bank account and that it has
                                accounts available for syncing.
                              </p>
                            </div>
                          </Async.Empty>
                          <Async.Success>
                            <div>
                              <p className="t-m-0 t-text-body-sm t-mb-4 t-text-text-60">
                                Select the accounts for which you wish to sync
                                the data for:
                              </p>

                              <div className=" t-items-center">
                                <div className="t-flex t-justify-between t-border-b t-border-solid t-border-neutral-0 t-border-0">
                                  <span className="t-text-body-sm t-text-text-30 t-pb-3 t-pl-7">
                                    Bank Account Name
                                  </span>
                                  <span className="t-text-body-sm t-text-text-30 t-pb-3 t-pr-[188px]">
                                    Currency
                                  </span>
                                </div>

                                {accounts?.map((account, index) => (
                                  <div
                                    key={account.mask}
                                    className="t-flex t-justify-between t-border-b t-border-solid t-border-neutral-0 t-border-0"
                                  >
                                    <div className=" t-py-3">
                                      <BankCheckBox
                                        account={account}
                                        onChange={(value) => {
                                          const new_account_mappers =
                                            values.account_mappers.slice();
                                          new_account_mappers[index].include =
                                            value;
                                          setFieldValue(
                                            `account_mappers`,
                                            new_account_mappers
                                          );
                                        }}
                                        name={`account_${index}`}
                                        checked={getIn(
                                          values,
                                          `account_mappers.${index}.include`
                                        )}
                                      />
                                    </div>

                                    <div className="t-w-60 t-py-3">
                                      <Combobox
                                        size="small"
                                        options={currencyOptions}
                                        menuPortalTarget={document.getElementById(
                                          "root"
                                        )}
                                        placeholder="Select currency"
                                        value={
                                          currencyOptions.find(
                                            (opt) =>
                                              opt.value ===
                                              getIn(
                                                values,
                                                `account_mappers.${index}.currency`
                                              )
                                          ) || null
                                        }
                                        onChange={(option) =>
                                          handleCurrencyChange(index, option)
                                        }
                                        isClearable={false}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Async.Success>
                        </Async.Root>
                      )}
                    </div>
                  </Modal.Body>
                  {currentStep === 2 && intermediateBanksSuccess && (
                    <Modal.FooterButtonGroup>
                      <Modal.RawClose asChild>
                        <Button>Cancel</Button>
                      </Modal.RawClose>
                      <Button
                        isLoading={isConfirmingMigration}
                        disabled={isConfirmingMigration}
                        customType="primary"
                        onClick={submitForm}
                      >
                        Connect
                      </Button>
                    </Modal.FooterButtonGroup>
                  )}
                </>
              );
            }}
          </FormikForm>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
